import React, { FC } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { HiInformationCircle } from 'react-icons/hi'
import { SwapProvider } from '@plug-core/providers/SwapProvider'
import { StakeButtonProps } from '@plug-core/interfaces/staking'
import { useUnstakeButton } from '@plug-core/hooks/useStaking'
import Button from '@components/Button'
import Prompt from '@components/Prompt'

const UnstakeButton: FC<StakeButtonProps> = ({ isLoading, ...props }) => {
  const { t } = useTranslation()

  const {
    setUnstakeMode,
    unstakeMode,
    onInfoClick,
    userError,
    reviewStake,
    setPrompt,
    prompt,
    delayedAmount,
    immediateAmount,
  } = useUnstakeButton(props)

  return (
    <Stack>
      <Stack flexDirection="row" mb={2}>
        <Box
          onClick={() => setUnstakeMode('immediate')}
          flex={1}
          bgcolor="rgba(255, 255, 255, 0.05)"
          p={1.25}
          mr={0.75}
          borderRadius={1}
          border={unstakeMode === 'immediate' ? '1px solid white' : 'none'}
          sx={{ cursor: 'pointer' }}
        >
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2" fontSize={16}>
              {t('stake.unstake.immediate.short')}
            </Typography>

            <HiInformationCircle onClick={(e) => onInfoClick(e, 'immediate')} size={20} />
          </Stack>

          <Typography variant="body1" fontSize={13} mt={0.25} color="text.light">
            {t('stake.unstake.receive')} {immediateAmount}
          </Typography>
        </Box>

        <Box
          flex={1}
          bgcolor="rgba(255, 255, 255, 0.05)"
          p={1.25}
          borderRadius={1}
          ml={0.75}
          border={unstakeMode === 'delayed' ? '1px solid white' : 'none'}
          sx={{ cursor: 'pointer' }}
          onClick={() => setUnstakeMode('delayed')}
        >
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2" fontSize={16}>
              {t('stake.unstake.delayed.short')}
            </Typography>

            <HiInformationCircle onClick={(e) => onInfoClick(e, 'delayed')} size={20} />
          </Stack>

          <Typography variant="body1" fontSize={13} mt={0.25} color="text.light">
            {t('stake.unstake.receive')} {delayedAmount}
          </Typography>
        </Box>
      </Stack>

      <Button
        variant="rainbow"
        disabled={!!userError || isLoading}
        onClick={reviewStake}
        fullWidth={true}
      >
        {userError || t('stake.unstakeNicp')}
      </Button>

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography textAlign="center" mt={1} mb={2} color="text.light">
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>
    </Stack>
  )
}

const UnstakeButtonWrapped = (props: StakeButtonProps) => (
  <SwapProvider>
    <UnstakeButton {...props} />
  </SwapProvider>
)

export { UnstakeButtonWrapped as UnstakeButton }
